/**
 * KBArticle — native сторінка статті у Mini App.
 *
 * Контент завантажується статично з `data/articles.json` (захардкоджено
 * скриптом `scripts/fetch_kb_articles.py` що тягне з telegra.ph API).
 * Це швидко (164KB total для 19 статей, кешується браузером) і дозволяє
 * рендерити статтю всередині WebApp без зовнішнього переходу.
 */

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { ArticleBottomNav } from "@/components/ArticleBottomNav";
import { FounderInstagramCard } from "@/components/FounderInstagramCard";
import { ScrollTopButton } from "@/components/ScrollTopButton";
import { openExternalOnAnchorClick } from "@/lib/openExternal";
import { clearKBStateIfLeavingTree } from "@/pages/KnowledgeBase";
import articlesData from "@/data/articles.json";

// ============================================================
// Types — node format з telegra.ph API
// ============================================================

type ContentNode =
  | string
  | {
      tag: string;
      attrs?: Record<string, string>;
      children?: ContentNode[];
    };

interface Article {
  title: string;
  author?: string;
  url?: string;
  content: ContentNode[];
  views?: number;
  error?: string;
}

const ARTICLES = articlesData as Record<string, Article>;

// Каталог: slug → category для breadcrumb / navigation
const CATEGORIES: Record<string, string> = {
  sales: "Продажі 💰",
  clients: "Клієнти 🤝",
  growth: "Ріст доходу 📈",
  mind: "Психологія і ресурс 🔋",
};

function categoryOf(slug: string): string | undefined {
  const prefix = slug.split("-")[0];
  return CATEGORIES[prefix];
}

// ============================================================
// Renderer — telegra.ph node → React
// ============================================================

/** Чи є цей node порожнім параграфом (telegra.ph пише `<p><br/></p>` як розрив-абзацу). */
function isEmptyParagraph(node: ContentNode): boolean {
  if (typeof node === "string") return node.trim() === "";
  if (node.tag !== "p") return false;
  const ch = node.children ?? [];
  if (ch.length === 0) return true;
  // Тільки <br> або порожні рядки усередині
  return ch.every(
    (c) =>
      (typeof c === "string" && c.trim() === "") ||
      (typeof c === "object" && c.tag === "br"),
  );
}

function renderNode(node: ContentNode, key: number | string): React.ReactNode {
  if (typeof node === "string") return node;

  const { tag, attrs = {}, children = [] } = node;

  // Порожній параграф = вертикальний пробіл (telegra.ph розрив між блоками)
  if (isEmptyParagraph(node)) {
    return <div key={key} className="h-4" aria-hidden />;
  }

  const rendered = children.map((c, i) => renderNode(c, i));

  switch (tag) {
    case "p":
      return (
        <p key={key} className="mb-4 text-[0.9375rem] leading-[1.7] text-ink">
          {rendered}
        </p>
      );
    case "h3":
    case "h4":
      return (
        <h3
          key={key}
          className="mt-6 mb-3 font-display text-[1.1875rem] font-bold text-ink leading-tight"
        >
          {rendered}
        </h3>
      );
    case "strong":
    case "b":
      return (
        <strong key={key} className="font-semibold text-ink">
          {rendered}
        </strong>
      );
    case "em":
    case "i":
      return (
        <em key={key} className="italic">
          {rendered}
        </em>
      );
    case "a":
      return (
        <a
          key={key}
          href={attrs.href}
          target={attrs.target ?? "_blank"}
          rel="noopener noreferrer"
          onClick={openExternalOnAnchorClick}
          className="text-primary underline underline-offset-2"
        >
          {rendered}
        </a>
      );
    case "ul":
      return (
        <ul key={key} className="my-3 list-disc pl-5 space-y-1.5 text-[0.9375rem] text-ink leading-relaxed">
          {rendered}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="my-3 list-decimal pl-5 space-y-1.5 text-[0.9375rem] text-ink leading-relaxed">
          {rendered}
        </ol>
      );
    case "li":
      return <li key={key}>{rendered}</li>;
    case "br":
      return <br key={key} />;
    case "blockquote":
      return (
        <blockquote
          key={key}
          className="my-4 border-l-2 border-primary pl-4 italic text-ink-dim"
        >
          {rendered}
        </blockquote>
      );
    case "hr":
      return <hr key={key} className="my-6 border-line" />;
    case "img":
      return (
        <img
          key={key}
          src={attrs.src?.startsWith("/") ? `https://telegra.ph${attrs.src}` : attrs.src}
          alt={attrs.alt ?? ""}
          className="my-4 w-full rounded-ios-card"
        />
      );
    case "figure":
      return <figure key={key} className="my-4">{rendered}</figure>;
    case "figcaption":
      return (
        <figcaption key={key} className="mt-2 text-center text-[0.75rem] text-ink-dim">
          {rendered}
        </figcaption>
      );
    case "iframe":
    case "video":
      return (
        <a
          key={key}
          href={attrs.src}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openExternalOnAnchorClick}
          className="my-4 block rounded-ios-card bg-surface-soft p-4 text-center text-[0.8125rem] text-primary"
        >
          ▶ Відкрити відео в браузері
        </a>
      );
    default:
      // Fallback — рендеримо як div
      return <div key={key}>{rendered}</div>;
  }
}

// ============================================================
// Page
// ============================================================

export function KBArticlePage() {
  // Той самий cleanup що в KnowledgeBasePage — щоб коли юзер з статті
  // йде на головну, sessionStorage чистився.
  useEffect(() => {
    return () => clearKBStateIfLeavingTree();
  }, []);

  const { slug = "" } = useParams<{ slug: string }>();
  const article = ARTICLES[slug];
  const category = categoryOf(slug);

  if (!article) {
    return (
      <>
        <BackNav to="/knowledge-base" backLabel="База знань" showMenu />
        <div className="px-4 py-8 text-center text-ink-dim">
          Статтю не знайдено.
        </div>
      </>
    );
  }

  if (article.error) {
    return (
      <>
        <BackNav to="/knowledge-base" backLabel="База знань" showMenu />
        <div className="px-4 py-8 text-center">
          <div className="text-ink-dim mb-3">
            Статтю не вдалось завантажити.
          </div>
          {article.url && (
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openExternalOnAnchorClick}
              className="text-primary underline"
            >
              Відкрити оригінал ↗
            </a>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <BackNav to="/knowledge-base" backLabel="База знань" showMenu />

      {/* Category meta. Owner 2026-09-12: брівка мусить стояти на тій самій
          висоті, що й на решті сторінок — BackNav mb-4 + pt-4 = 32 px від
          логотипа. */}
      {category && (
        <div className="mb-3 flex items-center gap-2 pt-4 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {category}
        </div>
      )}

      {/* Title */}
      <h1
        className="mb-4 font-display font-bold text-ink"
        style={{
          fontSize: "1.75rem",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
        }}
      >
        {article.title}
      </h1>

      {/* Meta */}
      {(article.author || article.views) && (
        <div className="mb-6 flex items-center gap-3 text-[0.75rem] text-ink-dim">
          {article.author && <span>{article.author}</span>}
          {article.author && article.views ? <span>·</span> : null}
          {article.views ? <span>{article.views} переглядів</span> : null}
        </div>
      )}

      {/* Content */}
      <article className="pb-4">
        {article.content.map((node, i) => renderNode(node, i))}
      </article>

      <FounderInstagramCard />

      {/* Низ статті: Назад (до бази знань) + Меню (юзер 2026-06-25) */}
      <ArticleBottomNav backTo="/knowledge-base" backLabel="База знань" />

      <ScrollTopButton />
    </>
  );
}
